import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { studentNotesApi } from "@/api";
import type { CreateStudentNoteData, StudentNoteKind } from "@/api";
import { QUERY_KEYS } from "@/constants";

/** Talaba haqidagi hamma qaydlar (admin/moderator hammasini ko'radi). */
export const useStudentNotes = (studentId: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_STUDENT_NOTES, studentId],
    queryFn: () => studentNotesApi.list(studentId),
    enabled: studentId > 0,
    select: res => res.data,
  });

const useInvalidateNotes = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_STUDENT_NOTES] });
};

export const useCreateStudentNote = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidateNotes();
  return useMutation({
    mutationFn: (data: CreateStudentNoteData) => studentNotesApi.create(data),
    onSuccess: () => {
      void invalidate();
      toast.success(t("NOTES.ADDED"));
    },
  });
};

export const useUpdateStudentNote = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidateNotes();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { kind?: StudentNoteKind; body?: string } }) =>
      studentNotesApi.update(id, data),
    onSuccess: () => {
      void invalidate();
      toast.success(t("NOTES.UPDATED"));
    },
  });
};

export const useDeleteStudentNote = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidateNotes();
  return useMutation({
    mutationFn: (id: number) => studentNotesApi.remove(id),
    onSuccess: () => {
      void invalidate();
      toast.success(t("NOTES.DELETED"));
    },
  });
};
